import React, { useMemo, useState } from "react";
import { Tabs, Tab, Typography, Box, TextField, IconButton, Divider, Chip, Tooltip, MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import GGLoaderButton from "../../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import SnackBar, { SnackTypes } from "../../../components/UtilityComponents/SnackBar/SnackBar";
import { PromotionsAPIHandler } from "../../../services/NetworkClient/BackendAPIs/PromotionsAPIHandler";
import { Promotion } from "../../../Models/Promotion";
import { formatCurrencyIndian } from "../../../utils/MoneyConversionUtils";

const AdminPromotionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [isLoadingActive, setIsLoadingActive] = useState(false);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fetchType, setFetchType] = useState<'all' | 'active' | 'byCode' | null>(null);
  const [codeSearch, setCodeSearch] = useState("");
  const [formState, setFormState] = useState<any>({
    id: "",
    code: "",
    description: "",
    termsAndConditions: [] as string[],
    enabled: true,
    discount: 0,
    discountType: "percent",
    startDate: "",
    endDate: "",
    minPurchaseAmount: 0,
    maxDiscountAmount: 0,
    eligibleGigs: [] as string[],
    eligibleCategories: [] as string[],
    eligibleSubcategories: [] as string[],
    usageLimit: 0,
    userLimit: 0,
    timesUsed: 0,
    applicableForNewUsersOnly: false,
    customRules: "",
  });

  // Snackbar
  const snackBarRef = React.useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({ message: "", state: SnackTypes.success })
  const notify = (message: string, state: SnackTypes) => { setSnackBarParams({ message, state }); snackBarRef.current?.showSnackbar(); }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);

  const fetchAllPromotions = async () => {
    setIsLoadingAll(true);
    try {
      const all = await PromotionsAPIHandler.shared.getAllPromos();
      setPromotions(all);
      setFetchType('all');
      notify(`Fetched ${all.length} total promotions`, SnackTypes.success);
    } catch (e) {
      notify("Failed to load all promotions", SnackTypes.error);
    }
    setIsLoadingAll(false);
  };

  const fetchActivePromotions = async () => {
    setIsLoadingActive(true);
    try {
      const active = await PromotionsAPIHandler.shared.getActivePromos();
      setPromotions(active);
      setFetchType('active');
      notify(`Fetched ${active.length} active promotions`, SnackTypes.success);
    } catch (e) {
      notify("Failed to load active promotions", SnackTypes.error);
    }
    setIsLoadingActive(false);
  };

  const fetchPromoByCode = async () => {
    if (!codeSearch.trim()) {
      notify("Please enter a promo code", SnackTypes.error);
      return;
    }
    setIsLoadingCode(true);
    try {
      const promo = await PromotionsAPIHandler.shared.getPromoByCode(codeSearch.trim());
      setPromotions([promo]);
      setFetchType('byCode');
      notify(`Found promotion: ${promo.code}`, SnackTypes.success);
    } catch (e) {
      notify(`Promotion with code "${codeSearch}" not found`, SnackTypes.error);
      setPromotions([]);
      setFetchType('byCode');
    }
    setIsLoadingCode(false);
  };

  const clearList = () => {
    setPromotions([]);
    setFetchType(null);
    setSearchQuery("");
    setCodeSearch("");
    setEditingId(null);
    notify("List cleared", SnackTypes.success);
  };

  const filteredPromos = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return promotions;
    return promotions.filter(p =>
      p.code.toLowerCase().includes(q) ||
      (p.description ?? "").toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q)
    );
  }, [searchQuery, promotions]);

  const resetForm = () => setFormState({
    id: "",
    code: "",
    description: "",
    termsAndConditions: [],
    enabled: true,
    discount: 0,
    discountType: "percent",
    startDate: "",
    endDate: "",
    minPurchaseAmount: 0,
    maxDiscountAmount: 0,
    eligibleGigs: [],
    eligibleCategories: [],
    eligibleSubcategories: [],
    usageLimit: 0,
    userLimit: 0,
    timesUsed: 0,
    applicableForNewUsersOnly: false,
    customRules: "",
  });

  const startCreate = () => { resetForm(); setEditingId("new"); };
  const startEdit = (p: Promotion) => { setFormState({
      id: p.id,
      code: p.code,
      description: p.description ?? "",
      termsAndConditions: p.termsAndConditions ?? [],
      enabled: p.enabled,
      discount: p.discount,
      discountType: p.discountType,
      startDate: p.startDate ? new Date(p.startDate).toISOString().slice(0,10) : "",
      endDate: p.endDate ? new Date(p.endDate).toISOString().slice(0,10) : "",
      minPurchaseAmount: p.minPurchaseAmount,
      maxDiscountAmount: p.maxDiscountAmount ?? 0,
      eligibleGigs: p.eligibleGigs ?? [],
      eligibleCategories: p.eligibleCategories ?? [],
      eligibleSubcategories: p.eligibleSubcategories ?? [],
      usageLimit: p.usageLimit ?? 0,
      userLimit: p.userLimit ?? 0,
      timesUsed: p.timesUsed ?? 0,
      applicableForNewUsersOnly: p.applicableForNewUsersOnly ?? false,
      customRules: p.customRules ? JSON.stringify(p.customRules, null, 2) : "",
    }); setEditingId(p.id); };

  const cancelEdit = () => { setEditingId(null); resetForm(); };

  const savePromo = async () => {
    const payload = { ...formState,
      // Convert string inputs to API-friendly
      startDate: formState.startDate ? new Date(formState.startDate).toISOString() : undefined,
      endDate: formState.endDate ? new Date(formState.endDate).toISOString() : undefined,
      termsAndConditions: (formState.termsAndConditions || []).filter((s: string) => s !== ""),
      eligibleGigs: (formState.eligibleGigs || []).filter((s: string) => s !== ""),
      eligibleCategories: (formState.eligibleCategories || []).filter((s: string) => s !== ""),
      eligibleSubcategories: (formState.eligibleSubcategories || []).filter((s: string) => s !== ""),
      customRules: (() => { try { return formState.customRules ? JSON.parse(formState.customRules) : undefined; } catch { return undefined; } })(),
    };
    setIsLoadingSave(true);
    try {
      if (editingId === "new") {
        const created = await PromotionsAPIHandler.shared.postPromo(payload);
        setPromotions(prev => [created, ...prev]);
      } else if (editingId) {
        const updated = await PromotionsAPIHandler.shared.updatePromo(editingId, payload);
        setPromotions(prev => prev.map(p => p.id === editingId ? updated : p));
      }
      notify("Saved successfully", SnackTypes.success);
      cancelEdit();
      // Refresh current view
      if (fetchType === 'all') fetchAllPromotions();
      else if (fetchType === 'active') fetchActivePromotions();
      else if (fetchType === 'byCode') fetchPromoByCode();
    } catch (e) {
      notify("Save failed", SnackTypes.error);
    }
    setIsLoadingSave(false);
  };

  const deletePromo = async (id: string) => {
    
    try {
      await PromotionsAPIHandler.shared.deletePromo(id);
      setPromotions(prev => prev.filter(p => p.id !== id));
      notify("Deleted", SnackTypes.success);
      // Refresh current view
      if (fetchType === 'all') fetchAllPromotions();
      else if (fetchType === 'active') fetchActivePromotions();
      else if (fetchType === 'byCode') fetchPromoByCode();
    } catch (e) {
      notify("Delete failed", SnackTypes.error);
    }
    
  };

  return (
    <div className="admin-order-page">
      <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={4000} message={snackBarParams.message} />
      
      {/* Compact Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        p: 2,
        borderRadius: 2,
        mb: 1,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
           Promotions Dashboard
        </Typography>
      </Box>

      {/* Main Content Card */}
      <Box sx={{
        background: 'white',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            background: '#f8fafc',
            px: 3,
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              height: 3,
              borderRadius: '2px 2px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#64748b',
              '&.Mui-selected': {
                color: '#1e293b'
              }
            }
          }}
        >
          <Tab label="📊 Manage Promotions" />
      </Tabs>

        <Box sx={{ p: 2 }}>
        {activeTab === 0 && (
          <Box>

            {/* Compact Fetch Controls */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center', 
              mb: 3, 
              flexWrap: 'wrap',
              p: 2,
              background: '#f8fafc',
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}>
              <GGLoaderButton 
                isLoading={isLoadingAll} 
                onClick={fetchAllPromotions} 
                variant="contained"
                size="small"
                sx={{ 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                  }
                }}
              >
                📋 All Promotions
              </GGLoaderButton>
              
              <GGLoaderButton 
                isLoading={isLoadingActive} 
                onClick={fetchActivePromotions} 
                variant="contained"
                size="small"
                sx={{ 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                  }
                }}
              >
                ✅ Active Only
              </GGLoaderButton>

              <GGLoaderButton 
                onClick={startCreate} 
                variant="contained" 
                isLoading={false}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                  }
                }}
              >
                <AddIcon sx={{ mr: 0.5 }} />New
              </GGLoaderButton>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
                <TextField 
                  size="small" 
                  placeholder="Search by code" 
                  value={codeSearch} 
                  onChange={(e) => setCodeSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchPromoByCode()}
                  sx={{ minWidth: '200px' }}
                />
                <IconButton 
                  onClick={fetchPromoByCode} 
                  disabled={!codeSearch.trim()}
                  sx={{ 
                    background: '#3b82f6',
                    color: 'white',
                    '&:hover': {
                      background: '#2563eb'
                    },
                    '&:disabled': {
                      background: '#e2e8f0',
                      color: '#94a3b8'
                    }
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Status Panel */}
            {fetchType && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2,
                p: 1.5, 
                borderRadius: 2, 
                background: fetchType === 'active' ? '#e8f5e8' : fetchType === 'all' ? '#e3f2fd' : '#f3e8ff',
                border: `1px solid ${fetchType === 'active' ? '#4caf50' : fetchType === 'all' ? '#2196f3' : '#8b5cf6'}`,
                minHeight: '40px'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {fetchType === 'all' ? '📋 All Promotions' : 
                   fetchType === 'active' ? '✅ Active Promotions' : 
                   `🔍 Search Result: "${codeSearch}"`} • {promotions.length} items
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {<GGLoaderButton 
                      variant="outlined" 
                      onClick={clearList}
                      isLoading={false}
                      size="small"
                      sx={{ color: '#64748b', borderColor: '#d1d5db' }}
                    >
                      Clear
                    </GGLoaderButton>
                  }
                </Box>
              </Box>
            )}

            {/* Search Filter */}
            {promotions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <TextField 
                  size="small" 
                  placeholder="Filter current results..." 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: '300px' }}
                />
              </Box>
            )}

            {editingId && (
              <Box sx={{ 
                p: 2, 
                border: '1px solid #e2e8f0', 
                borderRadius: 2, 
                mb: 2,
                background: '#f8fafc'
              }}>
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} marginBottom={'20px'}>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#1e293b' }}>
                      {editingId === 'new' ? 'Create Promotion' : `Edit Promotion #${formState.code}`}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <GGLoaderButton 
                        isLoading={isLoadingSave} 
                        onClick={savePromo} 
                        variant="contained"
                        size="small"
                        colorHex="#228B22"
                      >
                        <SaveIcon sx={{ mr: 0.5 }} />Save
                      </GGLoaderButton>
                      <GGLoaderButton 
                        variant="contained" 
                        onClick={cancelEdit} 
                        isLoading={false}
                        size="small"
                        colorHex="#8B0000"
                      >
                        <CloseIcon sx={{ mr: 0.5 }} />Cancel
                      </GGLoaderButton>
                    </Box>
                  </Box>
                

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(200px, 1fr))', gap: 2 }}>
                  <TextField label="Code" value={formState.code} onChange={e => setFormState((s: any) => ({ ...s, code: e.target.value }))} />
                  <TextField label="Description" value={formState.description} onChange={e => setFormState((s: any) => ({ ...s, description: e.target.value }))} />
                  <FormControl>
                    <InputLabel>Discount Type</InputLabel>
                    <Select label="Discount Type" value={formState.discountType} onChange={(e) => setFormState((s: any) => ({ ...s, discountType: e.target.value }))}>
                      <MenuItem value="percent">Percent</MenuItem>
                      <MenuItem value="absolute">Absolute</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Discount" type="number" value={formState.discount} onChange={e => setFormState((s: any) => ({ ...s, discount: Number(e.target.value) }))} />
                  <TextField label="Min Purchase Amount (in paisa)" type="number" value={formState.minPurchaseAmount} onChange={e => setFormState((s: any) => ({ ...s, minPurchaseAmount: Number(e.target.value) }))} />
                  <TextField label="Max Discount Amount (in paisa)" type="number" value={formState.maxDiscountAmount} onChange={e => setFormState((s: any) => ({ ...s, maxDiscountAmount: Number(e.target.value) }))} />
                  <TextField label="Start Date" type="date" InputLabelProps={{ shrink: true }} value={formState.startDate} onChange={e => setFormState((s: any) => ({ ...s, startDate: e.target.value }))} />
                  <TextField label="End Date" type="date" InputLabelProps={{ shrink: true }} value={formState.endDate} onChange={e => setFormState((s: any) => ({ ...s, endDate: e.target.value }))} />
                  <FormControlLabel control={<Switch checked={formState.enabled} onChange={(e) => setFormState((s: any) => ({ ...s, enabled: e.target.checked }))} />} label="Enabled" />
                  <FormControlLabel control={<Switch checked={formState.applicableForNewUsersOnly} onChange={(e) => setFormState((s: any) => ({ ...s, applicableForNewUsersOnly: e.target.checked }))} />} label="New Users Only" />
                  <TextField label="Usage Limit" type="number" value={formState.usageLimit} onChange={e => setFormState((s: any) => ({ ...s, usageLimit: Number(e.target.value) }))} />
                  <TextField label="User Limit" type="number" value={formState.userLimit} onChange={e => setFormState((s: any) => ({ ...s, userLimit: Number(e.target.value) }))} />
                  <TextField label="Times Used" type="number" value={formState.timesUsed} onChange={e => setFormState((s: any) => ({ ...s, timesUsed: Number(e.target.value) }))} />
                  <TextField label="Terms & Conditions (comma separated)" value={(formState.termsAndConditions || []).join(", ")} onChange={e => setFormState((s: any) => ({ ...s, termsAndConditions: e.target.value.split(",").map((x: string) => x.trim()) }))} />
                  <TextField label="Eligible Gigs (IDs, comma separated)" value={(formState.eligibleGigs || []).join(", ")} onChange={e => setFormState((s: any) => ({ ...s, eligibleGigs: e.target.value.split(",").map((x: string) => x.trim()) }))} />
                  <TextField label="Eligible Categories (IDs, comma separated)" value={(formState.eligibleCategories || []).join(", ")} onChange={e => setFormState((s: any) => ({ ...s, eligibleCategories: e.target.value.split(",").map((x: string) => x.trim()) }))} />
                  <TextField label="Eligible Subcategories (IDs, comma separated)" value={(formState.eligibleSubcategories || []).join(", ")} onChange={e => setFormState((s: any) => ({ ...s, eligibleSubcategories: e.target.value.split(",").map((x: string) => x.trim()) }))} />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <TextField label="Custom Rules (JSON)" value={formState.customRules} onChange={e => setFormState((s: any) => ({ ...s, customRules: e.target.value }))} minRows={4} multiline fullWidth />
                </Box>
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* List */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 2 }}>
              {(isLoadingAll || isLoadingActive || isLoadingCode) && promotions.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 4, 
                  color: '#64748b', 
                  fontSize: '1.1rem',
                  background: '#f8fafc',
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}>
                  Loading promotions...
                </Box>
              ) : filteredPromos.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 4, 
                  color: '#64748b',
                  background: '#f8fafc',
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>📋</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>No promotions found</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>Try fetching promotions or create a new one</Typography>
                </Box>
              ) : (
                filteredPromos.map(p => (
                  <Box key={p.id} sx={{ 
                    borderLeft: '2px solid',
                    borderColor: fetchType === 'active' ? '#10b981' : fetchType === 'all' ? '#3b82f6' : '#8b5cf6',
                    borderRadius: 3, 
                    p: 2, 
                    background: 'white', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                      transform: 'translateY(-4px)'
                    },
                    ...(fetchType === 'active' && p.enabled && {
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.02) 0%, rgba(255,255,255,1) 100%)'
                    }),
                    ...(fetchType === 'all' && {
                      background: 'linear-gradient(135deg, rgba(59,130,246,0.02) 0%, rgba(255,255,255,1) 100%)'
                    })
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1, pr: 2 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#1e293b' }}>{p.code}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                          {p.description || 'No description provided'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Promotion">
                          <IconButton 
                            size="small" 
                            onClick={() => startEdit(p)}
                            sx={{ 
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              '&:hover': { 
                                background: '#e3f2fd',
                                borderColor: '#3b82f6'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Promotion">
                          <IconButton 
                            size="small" 
                            onClick={() => deletePromo(p.id)}
                            sx={{ 
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              '&:hover': { 
                                background: '#ffebee',
                                borderColor: '#ef4444'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                      gap: 1, 
                      mt: 2,
                      p: 1,
                      background: '#f8fafc',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0'
                    }}>
                      <DataRow label="Discount" value={`${p.discount}${p.discountType === 'percent' ? '%' : ''}`} />
                      <DataRow label="Type" value={p.discountType} />
                      <DataRow label="Min Purchase" value={`${formatCurrencyIndian(p.minPurchaseAmount)}`} />
                      <DataRow label="Max Discount" value={p.maxDiscountAmount ? `${formatCurrencyIndian(p.maxDiscountAmount)}` : '—'} />
                      <DataRow label="Enabled" value={p.enabled ? 'Yes' : 'No'} />
                      <DataRow label="New Users Only" value={p.applicableForNewUsersOnly ? 'Yes' : 'No'} />
                      <DataRow label="Usage Limit" value={p.usageLimit ?? 0} />
                      <DataRow label="User Limit" value={p.userLimit ?? 0} />
                      <DataRow label="Times Used" value={p.timesUsed ?? 0} />
                      <DataRow label="Start Date" value={p.startDate ? new Date(p.startDate).toLocaleDateString() : '—'} />
                      <DataRow label="End Date" value={p.endDate ? new Date(p.endDate).toLocaleDateString() : '—'} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {(p.termsAndConditions ?? []).map((t, i) => <Chip key={i} size="small" label={t} sx={{ background: '#e2e8f0', color: '#374151' }} />)}
                      {(p.eligibleCategories ?? []).map((t, i) => <Chip key={`cat-${i}`} size="small" color="primary" variant="outlined" label={`Cat ${t}`} />)}
                      {(p.eligibleSubcategories ?? []).map((t, i) => <Chip key={`sub-${i}`} size="small" color="secondary" variant="outlined" label={`Sub ${t}`} />)}
                      {(p.eligibleGigs ?? []).map((t, i) => <Chip key={`gig-${i}`} size="small" variant="outlined" label={`Gig ${t}`} />)}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}
        </Box>
      </Box>
    </div>
  );
};

const DataRow = ({ label, value }: { label: string, value: any }) => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
    <Typography variant="caption" color="text.secondary">{label}:</Typography>
    <Typography variant="body2" fontWeight={600}>{String(value)}</Typography>
  </Box>
);

export default AdminPromotionsPage;
